import { useEffect, useRef, useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionTemplate,
  useReducedMotion,
} from "framer-motion";

/**
 * The develop.
 *
 * An image enters flat and underexposed and develops to full colour as it
 * travels up the viewport, the way a print comes up in a tray. It is bound to
 * scroll position rather than fired once, so scrolling back up undevelops it.
 *
 * Reduced motion gets the finished print immediately.
 */
export function Develop({ src, alt, className = "", sizes, loading = "lazy" }) {
  const ref = useRef(null);
  const reduced = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "center 0.62"],
  });

  const saturate = useTransform(scrollYProgress, [0, 1], [0.1, 1]);
  const brightness = useTransform(scrollYProgress, [0, 1], [0.68, 1]);
  const contrast = useTransform(scrollYProgress, [0, 1], [1.3, 1]);
  const filter = useMotionTemplate`saturate(${saturate}) brightness(${brightness}) contrast(${contrast})`;
  const opacity = useTransform(scrollYProgress, [0, 0.35], [0.45, 1]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.04, 1]);

  if (reduced) {
    return <img ref={ref} src={src} alt={alt} className={className} sizes={sizes} loading={loading} />;
  }

  return (
    <motion.img
      ref={ref}
      src={src}
      alt={alt}
      className={className}
      sizes={sizes}
      loading={loading}
      style={{ filter, opacity, scale, position: "relative", willChange: "filter, opacity, transform" }}
    />
  );
}

/**
 * Media query as state. Used to keep the pointer-driven and touch-driven
 * versions of a section from both existing at once: an element hidden with
 * display:none has no box to measure, which is a scroll hook measuring nothing.
 */
export function useMatchMedia(query) {
  const [matches, setMatches] = useState(() =>
    typeof matchMedia === "undefined" ? false : matchMedia(query).matches
  );

  useEffect(() => {
    const mq = matchMedia(query);
    const sync = () => setMatches(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, [query]);

  return matches;
}

/** Small entrance for text. Once, never on the way back up. */
export function Reveal({ children, delay = 0, as = "div", className = "" }) {
  const Tag = motion[as] || motion.div;
  return (
    <Tag
      className={className}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-12% 0px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </Tag>
  );
}
